// Overview — every system at a glance. Click a card to open its tab.

import type { ReadoutOverview, SystemCard } from '../../services/readout';
import { VALUE_NOT_RECORDED, formatAge, formatMetric, formatNet, formatUsd, moneyLine, netTone, systemCategoryLabel } from './readoutFormat';
import styles from './ReadoutPage.module.css';

interface Props {
  overview: ReadoutOverview;
  onSelect: (systemKey: string) => void;
}

const STATUS_CHIP: Record<string, string> = {
  active: styles.chipActive,
  paused: styles.chipPaused,
  retired: styles.chipRetired,
  building: styles.chipBuilding,
  planned: styles.chipBuilding,
};

function toneClass(tone: 'good' | 'bad' | 'neutral'): string {
  if (tone === 'good') return styles.good;
  if (tone === 'bad') return styles.bad;
  return '';
}

function liveNote(card: SystemCard): string | null {
  switch (card.live_status) {
    case 'partial':
      return 'some live numbers unavailable';
    case 'unavailable':
      return 'live numbers unavailable';
    case 'none':
      return 'no live reader yet';
    default:
      return null;
  }
}

export function OverviewTab({ overview, onSelect }: Props) {
  const t = overview.totals;
  const hasValue = t.net_usd != null && t.systems_with_value > 0;
  const netClass = toneClass(netTone(t));
  const windowLabel = `last ${overview.window_days} days`;
  const valueCoverage = `${t.systems_with_value} of ${t.systems} systems have a value`;

  return (
    <>
      <div className={styles.totals} data-testid="readout-totals">
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Systems</div>
          <div className={styles.kpiValue}>{t.systems}</div>
          <div className={styles.kpiSub}>{t.active_systems} active</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Cost · {windowLabel}</div>
          <div className={styles.kpiValue}>{formatUsd(t.cost_usd)}</div>
          <div className={styles.kpiSub}>
            AI spend {formatUsd(t.ai_spend_usd)}
            {t.unattributed_ai_spend_usd > 0 && ` · ${formatUsd(t.unattributed_ai_spend_usd)} unattributed`}
          </div>
        </div>
        <div className={styles.kpi} data-testid="readout-total-benefit">
          <div className={styles.kpiLabel}>Benefit · {windowLabel}</div>
          <div className={styles.kpiValue}>{hasValue ? formatUsd(t.benefit_usd) : '—'}</div>
          <div className={styles.kpiSub}>
            {hasValue ? `recorded by the curator · ${valueCoverage}` : `not recorded yet · ${valueCoverage}`}
          </div>
        </div>
        <div className={styles.kpi} data-testid="readout-total-net">
          <div className={styles.kpiLabel}>Net · {windowLabel}</div>
          <div className={`${styles.kpiValue} ${netClass}`}>{formatNet(t.net_usd)}</div>
          <div className={styles.kpiSub}>
            {hasValue ? `benefit minus cost · ${valueCoverage}` : VALUE_NOT_RECORDED}
          </div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Funding · {windowLabel}</div>
          <div className={styles.kpiValue}>{formatUsd(t.funding_usd)}</div>
          <div className={styles.kpiSub}>what pays for the work</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Readouts</div>
          <div className={styles.kpiValue}>
            <span className={styles.good}>{t.narratives_fresh}</span>
            <span className={styles.kpiSub}> fresh</span>
          </div>
          <div className={styles.kpiSub}>
            <span className={t.narratives_stale > 0 ? styles.warn : ''}>{t.narratives_stale} stale</span>
            {' · '}
            {t.narratives_missing} not written yet
          </div>
        </div>
      </div>

      <div className={styles.cards}>
        {overview.systems.map((card) => {
          const note = liveNote(card);
          return (
            <button
              key={card.system.key}
              type="button"
              className={styles.card}
              onClick={() => onSelect(card.system.key)}
              data-testid={`readout-card-${card.system.key}`}
            >
              <div className={styles.cardHead}>
                <span className={styles.cardName}>{card.system.name}</span>
                <span className={`${styles.chip} ${STATUS_CHIP[card.system.status] ?? ''}`}>{card.system.status}</span>
              </div>
              {card.narrative_headline ? (
                <div className={styles.cardHeadline}>{card.narrative_headline}</div>
              ) : (
                <div className={styles.cardHeadlineMissing}>No readout written yet.</div>
              )}
              <div className={styles.cardMeta}>
                <span>{systemCategoryLabel(card.system.category)}</span>
                {card.system.owner && <span>· {card.system.owner}</span>}
                <span>· readout {formatAge(card.narrative_age_days)}</span>
                {note && <span className={styles.warn}>· {note}</span>}
              </div>
              {card.headline_metrics.length > 0 && (
                <div className={styles.cardMetrics}>
                  {card.headline_metrics.map((m) => (
                    <div key={m.metric_key} className={styles.mini} title={m.detail ?? undefined}>
                      <div className={styles.miniValue}>{formatMetric(m)}</div>
                      <div className={styles.miniLabel}>{m.label}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className={`${styles.cardMoney} ${toneClass(netTone(card.money))}`}>{moneyLine(card.money)}</div>
            </button>
          );
        })}
      </div>
    </>
  );
}
